import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import useSeo from "@/hooks/useSeo";
import NotFound from "@/pages/NotFound";
import { blogLd, breadcrumbLd } from "@/lib/jsonld";
import { useBlogStatus } from "@/lib/useBlogStatus";
import { formatDatumNL } from "@/lib/datum";
import { ContentLoading } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export default function Blog() {
    const { loaded, showBlog } = useBlogStatus();
    const [params, setParams] = useSearchParams();
    const page = Math.max(1, parseInt(params.get("pagina") || "1", 10) || 1);
    const [data, setData] = useState(null);

    useSeo({
        title: "Blog | Human Design Studio",
        description: "Artikelen over Human Design door Marcel van der Veen.",
        path: "/blog",
        jsonLd: showBlog ? [blogLd(), breadcrumbLd("Blog", "/blog")] : undefined,
    });

    useEffect(() => {
        if (!showBlog) return;
        let alive = true;
        axios
            .get(`${API}/blog/posts`, { params: { page } })
            .then((r) => alive && setData(r.data))
            .catch(() => alive && setData({ posts: [], total: 0, page: 1, pages: 1 }));
        return () => {
            alive = false;
        };
    }, [page, showBlog]);

    // Verborgen zolang er 0 gepubliceerde artikelen zijn
    if (!loaded) return <ContentLoading />;
    if (!showBlog) return <NotFound />;
    if (!data) return <ContentLoading />;

    const goTo = (p) => setParams(p > 1 ? { pagina: String(p) } : {});

    return (
        <div data-testid="page-blog">
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pt-24 md:pt-40 pb-10 md:pb-16">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6"
                >
                    Blog
                </motion.p>
                <div className="overflow-hidden">
                    <motion.h1
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1.05, ease: EASE, delay: 0.05 }}
                        className="font-[Fraunces] text-[16vw] md:text-[9vw] lg:text-[96px] leading-[0.98] tracking-[-0.02em] text-[var(--hds-ink)]"
                        style={{ fontWeight: 300 }}
                    >
                        Blog
                    </motion.h1>
                </div>
            </section>

            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-40">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="blog-grid">
                    {data.posts.map((post, i) => (
                        <motion.article
                            key={post.slug}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{ duration: 0.8, ease: EASE, delay: (i % 3) * 0.08 }}
                            data-testid={`blog-card-${post.slug}`}
                            className="group flex flex-col rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] overflow-hidden shadow-[0_20px_60px_rgba(44,39,34,0.04)] hover:shadow-[0_28px_80px_rgba(44,39,34,0.08)] transition-shadow duration-700"
                        >
                            {post.featured_image && (
                                <Link to={`/blog/${post.slug}`} tabIndex={-1} aria-hidden>
                                    <img
                                        src={post.featured_image}
                                        alt={post.featured_alt || ""}
                                        className="w-full aspect-[16/10] object-cover object-[center_25%]"
                                        loading="lazy"
                                    />
                                </Link>
                            )}
                            <div className="flex flex-col flex-1 p-7 md:p-8">
                                <p className="text-[11.5px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-3">
                                    {formatDatumNL(post.published_at)}
                                </p>
                                <h2
                                    className="font-[Fraunces] text-[24px] md:text-[27px] leading-[1.12] tracking-[-0.01em] text-[var(--hds-ink)] mb-3"
                                    style={{ fontWeight: 400 }}
                                >
                                    <Link to={`/blog/${post.slug}`} className="hover:text-[var(--hds-clay)] transition-colors duration-300">
                                        {post.title}
                                    </Link>
                                </h2>
                                {post.excerpt && (
                                    <p className="text-[15px] leading-[1.6] text-[var(--hds-ink)]/75 flex-1">
                                        {post.excerpt}
                                    </p>
                                )}
                                <Link
                                    to={`/blog/${post.slug}`}
                                    data-testid={`blog-lees-verder-${post.slug}`}
                                    className="mt-5 inline-flex items-center gap-3 text-[13.5px] text-[var(--hds-ink)] group/link self-start"
                                >
                                    <span className="inline-block w-8 h-px bg-[var(--hds-clay)] transition-all duration-500 group-hover/link:w-12" />
                                    Lees verder
                                </Link>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {data.pages > 1 && (
                    <nav className="mt-14 flex items-center justify-center gap-2" aria-label="Paginering" data-testid="blog-pagination">
                        {Array.from({ length: data.pages }, (_, k) => k + 1).map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => goTo(p)}
                                aria-current={p === data.page ? "page" : undefined}
                                data-testid={`blog-page-${p}`}
                                className={`w-11 h-11 rounded-full border text-[14px] transition-colors duration-300 ${
                                    p === data.page
                                        ? "bg-[var(--hds-clay)] border-[var(--hds-clay)] text-[var(--hds-card)]"
                                        : "border-[var(--hds-line)] text-[var(--hds-ink)]/70 hover:border-[var(--hds-clay)]"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </nav>
                )}
            </section>
        </div>
    );
}
